import React, { useEffect } from 'react'
import { getUserContainer } from '../../container/user-module'
import { USER_API_KEY } from '../../modules/users'
import { IUserApi } from '../../modules/users/api/UserApi'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from 'common/status/StatusService'

export const GoogleCallbackHandler: React.FC = () => {
  useEffect(() => {
    if (window.location.pathname === '/auth/google/callback') {
      const userContainer = getUserContainer()
      const userApi = userContainer.get<IUserApi>(USER_API_KEY)
      const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
      const statusService = userContainer.get<IStatusService>(STATUS_SERVICE_KEY)

      const processCallback = async () => {
        let lastError: any = null

        const statusSub = statusService.statusObservable().subscribe(async (status: any) => {
          if (status.variant === 'error' && status.error) {
            // Si es una Promise, resolverla
            if (status.error instanceof Promise) {
              try {
                lastError = await status.error
              } catch (e) {
                lastError = e
              }
            } else {
              lastError = status.error
            }
          }
        })

        try {
          const urlParams = new URLSearchParams(window.location.search)
          const provider = 'google'
          const code = urlParams.get('code')
          const state = urlParams.get('state')

          if (!code) {
            throw new Error('MISSING_PARAMETERS')
          }

          const operation = state?.startsWith('registration:') ? 'registration' : 'login'
          const registrationToken = state?.startsWith('registration:')
            ? state.replace('registration:', '')
            : state || ''

          let result

          if (operation === 'login') {
            result = await new Promise((resolve, reject) => {
              const subscription = loggedUserService
                .loginWithOAuth(code, state ?? undefined, provider)
                .subscribe({
                  next: (value) => {
                    if (value) {
                      resolve(value)
                      subscription.unsubscribe()
                    } else {
                      setTimeout(() => {
                        const errorToUse = lastError || new Error('USER_NOT_FOUND')
                        reject(errorToUse)
                        subscription.unsubscribe()
                      }, 200)
                    }
                  },
                  error: async (error) => {
                    if (error instanceof Promise) {
                      try {
                        const resolvedError = await error
                        reject(resolvedError)
                      } catch (e) {
                        reject(e)
                      }
                    } else {
                      reject(error)
                    }
                    subscription.unsubscribe()
                  },
                  complete: () => {
                    console.log('[GoogleCallback] Login complete')
                  },
                })
            })
          } else {
            result = await new Promise((resolve, reject) => {
              const subscription = userApi
                .registerWithOAuth(provider, code, registrationToken)
                .subscribe({
                  next: (value) => {
                    if (value) {
                      resolve(value)
                      subscription.unsubscribe()
                    } else {
                      setTimeout(() => {
                        const errorToUse = lastError || new Error('REGISTRATION_FAILED')
                        reject(errorToUse)
                        subscription.unsubscribe()
                      }, 200)
                    }
                  },
                  error: async (error) => {
                    if (error instanceof Promise) {
                      try {
                        const resolvedError = await error
                        reject(resolvedError)
                      } catch (e) {
                        reject(e)
                      }
                    } else {
                      reject(error)
                    }
                    subscription.unsubscribe()
                  },
                  complete: () => {
                    console.log('[GoogleCallback] Registration complete')
                  },
                })
            })
          }

          statusSub.unsubscribe()

          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'GOOGLE_OAUTH_SUCCESS',
                user: result,
                operation: operation,
              },
              window.location.origin
            )
          }
        } catch (error: any) {
          statusSub.unsubscribe()

          let errorCode = 'UNKNOWN_ERROR'
          let errorMessage = 'Error en la autenticación'
          let originalError = 'Unknown error'

          if (error) {
            const errorMsg = error.message || error.error || error.toString() || ''
            originalError = errorMsg
            const errorString = errorMsg.toLowerCase()

            if (errorString.includes('user_not_found') || errorString === 'user_not_found') {
              errorCode = 'USER_NOT_FOUND'
              errorMessage = 'Usuario no encontrado. Por favor, regístrate primero.'
            } else if (errorString.includes('email_already_exists')) {
              errorCode = 'EMAIL_ALREADY_EXISTS'
              errorMessage = 'El email ya está registrado'
            } else if (errorString.includes('invalid_password')) {
              errorCode = 'INVALID_PASSWORD'
              errorMessage = 'Contraseña incorrecta'
            } else if (errorString.includes('google_auth_failed')) {
              errorCode = 'GOOGLE_AUTH_FAILED'
              errorMessage = 'Error al autenticar con Google'
            } else if (errorString) {
              errorCode = errorString.toUpperCase()
              errorMessage = errorMsg
            }
          }

          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'GOOGLE_OAUTH_ERROR',
                error: errorMessage,
                errorCode: errorCode,
                originalError: originalError,
              },
              window.location.origin
            )
          }
        } finally {
          setTimeout(() => {
            window.close()
          }, 1000)
        }
      }

      processCallback()
    }
  }, [])

  return null
}
